import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { trackEvent } from "@/lib/track";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Check,
  Palette,
  Layers,
  Anchor,
  Users,
  Sun,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint 1 Vision AI — 5-step opt-in wizard collecting the user's taste
 * profile. Backend endpoint accepts partial updates so each Skip still
 * persists the axes that WERE answered. Skipped axes stay null.
 */

const STYLE_OPTIONS = [
  { id: "ART_DECO",   img: "/vision/styles/ART_DECO.jpg" },
  { id: "CLASSIC",    img: "/vision/styles/CLASSIC.jpg" },
  { id: "MODERN",     img: "/vision/styles/MODERN.jpg" },
  { id: "NEOCLASSIC", img: "/vision/styles/NEOCLASSIC.jpg" },
];

const STYLE_EXAMPLES = {
  ART_DECO: [
    "/vision/styles/art_deco/ART_DECO_ex1.jpg",
    "/vision/styles/art_deco/ART_DECO_ex2.jpg",
    "/vision/styles/art_deco/ART_DECO_ex3.jpg",
    "/vision/styles/art_deco/ART_DECO_ex4.jpg",
  ],
  CLASSIC: [
    "/vision/styles/classic/CLASSIC_ex1.jpg",
    "/vision/styles/classic/CLASSIC_ex2.jpg",
    "/vision/styles/classic/CLASSIC_ex3.jpg",
    "/vision/styles/classic/CLASSIC_ex4.jpg",
  ],
  MODERN: [
    "/vision/styles/modern/MODERN_ex1.jpg",
    "/vision/styles/modern/MODERN_ex2.jpg",
    "/vision/styles/modern/MODERN_ex3.jpg",
    "/vision/styles/modern/MODERN_ex4.jpg",
    "/vision/styles/modern/MODERN_ex5.jpg",
  ],
  NEOCLASSIC: [
    "/vision/styles/neoclassic/NEOCLASSIC_ex1.jpg",
    "/vision/styles/neoclassic/NEOCLASSIC_ex2.jpg",
    "/vision/styles/neoclassic/NEOCLASSIC_ex3.jpg",
    "/vision/styles/neoclassic/NEOCLASSIC_ex4.jpg",
    "/vision/styles/neoclassic/NEOCLASSIC_ex5.jpg",
    "/vision/styles/neoclassic/NEOCLASSIC_ex6.jpg",
  ],
};

const DENSITY_OPTIONS = ["minimalist", "balanced", "rich", "spectacular"];
const FIDELITY_OPTIONS = ["keep_all", "moderate", "bold"];
const AUDIENCE_OPTIONS = ["private_client", "showroom", "social", "commercial"];
const MOOD_OPTIONS = ["warm", "neutral", "cool", "dramatic"];

const STEP_META = [
  { key: "preferred_style",  icon: Palette, titleKey: "vision_ai.step1_title", bodyKey: "vision_ai.step1_body" },
  { key: "ornament_density", icon: Layers,  titleKey: "vision_ai.step2_title", bodyKey: "vision_ai.step2_body" },
  { key: "source_fidelity",  icon: Anchor,  titleKey: "vision_ai.step3_title", bodyKey: "vision_ai.step3_body" },
  { key: "target_audience",  icon: Users,   titleKey: "vision_ai.step4_title", bodyKey: "vision_ai.step4_body" },
  { key: "mood_light",       icon: Sun,     titleKey: "vision_ai.step5_title", bodyKey: "vision_ai.step5_body" },
];

function OptionCard({ selected, onClick, title, desc, img, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-selected={selected ? "true" : "false"}
      className={`relative w-full text-left rounded-lg border transition-all p-4 overflow-hidden group ${
        selected
          ? "border-orange-500 bg-orange-500/10 shadow-[0_0_0_1px_rgba(255,87,34,0.4)]"
          : "border-white/10 bg-zinc-950/50 hover:border-orange-500/40 hover:bg-zinc-900/60"
      }`}
    >
      {img && (
        <div className="relative h-32 -mx-4 -mt-4 mb-3 overflow-hidden">
          <img
            src={img}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-outfit font-semibold text-white tracking-tight">{title}</div>
          {desc && <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{desc}</div>}
        </div>
        {selected && (
          <div className="shrink-0 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
            <Check size={14} strokeWidth={3} className="text-white" />
          </div>
        )}
      </div>
    </button>
  );
}

export default function VisionAiWizard({ open, onOpenChange, onSaved }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({
    preferred_style: null,
    ornament_density: null,
    source_fidelity: null,
    target_audience: null,
    mood_light: null,
    preferred_example: null,
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Preload existing profile when opening so editing keeps the current choices
  useEffect(() => {
    if (!open) return;
    setDone(false);
    setStep(0);
    api
      .get("/vision/profile")
      .then((r) => {
        const d = r.data || {};
        setProfile({
          preferred_style: d.preferred_style || null,
          ornament_density: d.ornament_density || null,
          source_fidelity: d.source_fidelity || null,
          target_audience: d.target_audience || null,
          mood_light: d.mood_light || null,
          preferred_example: d.preferred_example || null,
        });
      })
      .catch(() => {});
  }, [open]);

  const setAxis = (key, val) =>
    setProfile((p) => {
      const next = { ...p, [key]: val };
      // Reset the example selection when the style changes so a stale
      // "ART_DECO:2" doesn't stick after the user picks MODERN.
      if (key === "preferred_style" && p.preferred_style !== val) {
        next.preferred_example = null;
      }
      return next;
    });

  const toggleExample = (style, idx) => {
    setProfile((p) => {
      const marker = `${style}:${idx}`;
      const clearing = p.preferred_example === marker;
      return {
        ...p,
        preferred_style: style, // clicking an example implies choosing that style
        preferred_example: clearing ? null : marker,
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only send non-null values — skipped steps remain unchanged.
      // preferred_example is special: empty string clears a previous choice.
      const body = {};
      Object.entries(profile).forEach(([k, v]) => {
        if (v) body[k] = v;
      });
      // Explicit clear for preferred_example (avoids stale marker on DB).
      if (profile.preferred_style && !profile.preferred_example) {
        body.preferred_example = "";
      }
      if (Object.keys(body).length === 0) {
        toast.error(t("vision_ai.wizard_error"));
        setSaving(false);
        return;
      }
      await api.patch("/vision/profile", body);
      trackEvent("vision.profile.save", {
        preferred_style: profile.preferred_style,
        density: profile.ornament_density,
        preferred_example: profile.preferred_example || null,
        fields_set: Object.keys(body).length,
      });
      setDone(true);
      if (onSaved) onSaved(profile);
    } catch (e) {
      toast.error(t("vision_ai.wizard_error"));
    } finally {
      setSaving(false);
    }
  };

  const meta = STEP_META[step];
  const isLast = step === STEP_META.length - 1;

  const renderStepBody = () => {
    if (step === 0) {
      const examples = STYLE_EXAMPLES[profile.preferred_style];
      return (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {STYLE_OPTIONS.map((o) => (
              <OptionCard
                key={o.id}
                testId={`vision-style-${o.id}`}
                selected={profile.preferred_style === o.id}
                onClick={() => setAxis("preferred_style", o.id)}
                title={t(`vision_ai.style_${o.id}`)}
                desc={t(`vision_ai.style_${o.id}_desc`)}
                img={o.img}
              />
            ))}
          </div>
          {examples && examples.length > 0 && (
            <div
              className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-4"
              data-testid={`vision-style-examples-${profile.preferred_style}`}
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-orange-500 mb-1">
                <Sparkles size={12} strokeWidth={1.5} />
                {t("vision_ai.style_examples_label", { defaultValue: "Exemple de inspirație" })}
              </div>
              <div className="text-[11px] text-zinc-400 mb-3 leading-relaxed">
                {t("vision_ai.style_examples_hint", {
                  defaultValue: "Click pe o imagine pentru a-i spune AI-ului să urmeze acea sub-estetică.",
                })}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                {examples.map((src, idx) => {
                  const marker = `${profile.preferred_style}:${idx + 1}`;
                  const isSelected = profile.preferred_example === marker;
                  return (
                    <button
                      type="button"
                      key={src}
                      onClick={() => toggleExample(profile.preferred_style, idx + 1)}
                      data-testid={`vision-style-example-${profile.preferred_style}-${idx + 1}`}
                      data-selected={isSelected ? "true" : "false"}
                      className={`relative aspect-[4/3] overflow-hidden rounded-md border transition-all group ${
                        isSelected
                          ? "border-orange-500 shadow-[0_0_0_2px_rgba(255,87,34,0.35)]"
                          : "border-white/10 hover:border-orange-500/50"
                      }`}
                    >
                      <img
                        src={src}
                        alt={`${profile.preferred_style} example ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {isSelected && (
                        <>
                          <div className="absolute inset-0 bg-orange-500/10 pointer-events-none" />
                          <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center shadow-lg">
                            <Check size={14} strokeWidth={3} className="text-white" />
                          </div>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }
    if (step === 1) {
      return (
        <div className="grid sm:grid-cols-2 gap-3">
          {DENSITY_OPTIONS.map((id) => (
            <OptionCard
              key={id}
              testId={`vision-density-${id}`}
              selected={profile.ornament_density === id}
              onClick={() => setAxis("ornament_density", id)}
              title={t(`vision_ai.density_${id}`)}
              desc={t(`vision_ai.density_${id}_desc`)}
            />
          ))}
        </div>
      );
    }
    if (step === 2) {
      return (
        <div className="grid sm:grid-cols-3 gap-3">
          {FIDELITY_OPTIONS.map((id) => (
            <OptionCard
              key={id}
              testId={`vision-fidelity-${id}`}
              selected={profile.source_fidelity === id}
              onClick={() => setAxis("source_fidelity", id)}
              title={t(`vision_ai.fidelity_${id}`)}
              desc={t(`vision_ai.fidelity_${id}_desc`)}
            />
          ))}
        </div>
      );
    }
    if (step === 3) {
      return (
        <div className="grid sm:grid-cols-2 gap-3">
          {AUDIENCE_OPTIONS.map((id) => (
            <OptionCard
              key={id}
              testId={`vision-audience-${id}`}
              selected={profile.target_audience === id}
              onClick={() => setAxis("target_audience", id)}
              title={t(`vision_ai.audience_${id}`)}
              desc={t(`vision_ai.audience_${id}_desc`)}
            />
          ))}
        </div>
      );
    }
    return (
      <div className="grid sm:grid-cols-2 gap-3">
        {MOOD_OPTIONS.map((id) => (
          <OptionCard
            key={id}
            testId={`vision-mood-${id}`}
            selected={profile.mood_light === id}
            onClick={() => setAxis("mood_light", id)}
            title={t(`vision_ai.mood_${id}`)}
            desc={t(`vision_ai.mood_${id}_desc`)}
          />
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-zinc-950 border-white/10 text-white p-0 overflow-hidden"
        data-testid="vision-wizard-dialog"
      >
        {done ? (
          <div className="p-8 text-center" data-testid="vision-wizard-done">
            <div className="mx-auto w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center mb-4">
              <Check size={26} strokeWidth={2.5} className="text-white" />
            </div>
            <DialogHeader className="text-center">
              <DialogTitle className="font-outfit font-black text-2xl tracking-tight text-white">
                {t("vision_ai.wizard_saved_title")}
              </DialogTitle>
              <DialogDescription className="text-zinc-400 mt-2 text-sm">
                {t("vision_ai.wizard_saved_body")}
              </DialogDescription>
            </DialogHeader>
            <Button
              onClick={() => onOpenChange(false)}
              data-testid="vision-wizard-saved-close"
              className="mt-6 bg-orange-500 hover:bg-orange-600 text-white"
            >
              {t("vision_ai.wizard_saved_cta")}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="p-6 pb-4 border-b border-white/5 bg-gradient-to-br from-zinc-950 to-zinc-900">
              <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-2 flex items-center gap-2">
                <Sparkles size={12} strokeWidth={1.5} />
                {t("vision_ai.banner_eyebrow")}
              </div>
              <DialogTitle className="font-outfit font-black text-2xl sm:text-3xl tracking-tight text-white">
                {t("vision_ai.wizard_title")}
              </DialogTitle>
              <DialogDescription className="text-zinc-400 text-sm">
                {t("vision_ai.wizard_subtitle")}
              </DialogDescription>
              {/* Step indicator */}
              <div className="flex items-center gap-2 mt-4">
                {STEP_META.map((meta, i) => (
                  <div
                    key={meta.key}
                    className={`h-1 rounded-full flex-1 transition-colors ${
                      i <= step ? "bg-orange-500" : "bg-white/10"
                    }`}
                    data-testid={`vision-progress-${i}`}
                  />
                ))}
              </div>
              <div className="text-xs text-zinc-500 mt-2">
                {t("vision_ai.wizard_step_of", { n: step + 1 })}
              </div>
            </DialogHeader>

            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="flex items-center gap-2.5 text-white mb-1">
                <meta.icon size={18} strokeWidth={1.5} className="text-orange-500" />
                <h3 className="font-outfit font-bold text-lg tracking-tight">
                  {t(meta.titleKey)}
                </h3>
              </div>
              <p className="text-sm text-zinc-400 mb-5">{t(meta.bodyKey)}</p>
              {renderStepBody()}
            </div>

            <div className="p-4 sm:p-6 border-t border-white/5 flex items-center justify-between gap-3 bg-zinc-950">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0 || saving}
                data-testid="vision-wizard-back"
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft size={16} strokeWidth={1.5} />
                {t("vision_ai.wizard_back")}
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => (isLast ? save() : setStep((s) => s + 1))}
                  disabled={saving}
                  data-testid="vision-wizard-skip"
                  className="text-zinc-500 hover:text-zinc-300 text-xs"
                >
                  {t("vision_ai.wizard_skip")}
                </Button>
                {isLast ? (
                  <Button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="vision-wizard-save"
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        {t("vision_ai.wizard_saving")}
                      </>
                    ) : (
                      <>
                        <Check size={16} strokeWidth={2} />
                        {t("vision_ai.wizard_save")}
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={() => setStep((s) => s + 1)}
                    data-testid="vision-wizard-next"
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {t("vision_ai.wizard_next")}
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
